import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faDownload,
  faTable,
  faHistory,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import ActionButton from "../ui/ActionButton";
import "../../style/DonorInventory.css";

const COLORS = [
  "#669d2e",
  "#8bc34a",
  "#4caf50",
  "#81c784",
  "#a5d6a7",
  "#c5e1a5",
  "#dce775",
  "#aed581",
  "#7cb342",
  "#558b2f",
  "#33691e",
  "#76ff03",
];

// format relative time (e.g., "Just now", "5m ago", "2h ago", "3d ago")
const getTimeAgo = (dateString) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffSeconds < 60) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const MaterialsOverviewSection = ({ data, transactions = [], darkMode }) => {
  const { t } = useTranslation();
  const [selectedMaterials, setSelectedMaterials] = useState(new Set());

  const chartData = useMemo(() => {
    return data.map((item) => ({
      name: item.material,
      value: item.quantity,
      revenue: item.revenue,
      allocations: item.allocations,
    }));
  }, [data]);

  // filter transactions by selected materials
  const filteredTransactions = useMemo(() => {
    if (selectedMaterials.size === 0) return [];
    return transactions.filter((txn) => selectedMaterials.has(txn.material));
  }, [transactions, selectedMaterials]);

  // calculate totals
  const totals = useMemo(() => {
    return data.reduce(
      (acc, item) => ({
        quantity: acc.quantity + item.quantity,
        revenue: acc.revenue + item.revenue,
        allocations: acc.allocations + item.allocations,
      }),
      { quantity: 0, revenue: 0, allocations: 0 }
    );
  }, [data]);

  // toggle material selection
  const handleMaterialToggle = (material) => {
    setSelectedMaterials((prev) => {
      const next = new Set(prev);
      if (next.has(material)) {
        next.delete(material);
      } else {
        next.add(material);
      }
      return next;
    });
  };

  // export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Material Type",
      "Quantity (lbs)",
      "Revenue ($)",
      "Allocations",
    ];
    const rows = data.map((item) => [
      item.material,
      item.quantity.toFixed(2),
      item.revenue.toFixed(2),
      item.allocations,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `donor-materials-overview-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = ((item.value / totals.quantity) * 100).toFixed(1);
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{item.name}</p>
          <p>
            {item.value.toLocaleString()} lbs ({percentage}%)
          </p>
          <p>
            $
            {item.revenue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </p>
          <p>
            {item.allocations} {t("allocations")}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="donor-materials-overview-section">
      <div className="donor-materials-overview-grid">
        {/* Pie Chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-card-icon">
              <FontAwesomeIcon icon={faChartPie} />
            </div>
            <h5 className="chart-card-title">{t("Material Distribution")}</h5>
            {chartData.length > 0 && (
              <span className="chart-card-badge">
                {chartData.length} {t("types")}
              </span>
            )}
          </div>
          <div className="chart-card-content pie-chart-container">
            {chartData.length === 0 ? (
              <div className="chart-empty-state">
                <p>{t("No allocation data for selected period")}</p>
              </div>
            ) : (
              <>
                <div className="pie-chart-wrapper">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={85}
                        innerRadius={45}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            opacity={
                              selectedMaterials.size === 0 ||
                              selectedMaterials.has(entry.name)
                                ? 1
                                : 0.3
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="pie-chart-legend">
                  {chartData.map((entry, index) => (
                    <div key={`legend-${index}`} className="pie-legend-item">
                      <svg width="12" height="12" style={{ flexShrink: 0 }}>
                        <rect
                          x="0"
                          y="0"
                          width="12"
                          height="12"
                          rx="3"
                          fill={COLORS[index % COLORS.length]}
                        />
                      </svg>
                      <span className="pie-legend-label">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Materials Table */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div className="chart-card-icon">
              <FontAwesomeIcon icon={faTable} />
            </div>
            <h5 className="chart-card-title">{t("Materials Breakdown")}</h5>
            <ActionButton
              variant="secondary"
              icon={faDownload}
              onClick={handleExportCSV}
              disabled={data.length === 0}
              className="compact-btn"
            >
              {t("Export CSV")}
            </ActionButton>
          </div>

          <div className="donor-materials-table-wrapper">
            <table className="donor-materials-table">
              <thead>
                <tr>
                  <th className="checkbox-col"></th>
                  <th>{t("Material Type")}</th>
                  <th className="number-col">{t("Quantity (lbs)")}</th>
                  <th className="number-col">{t("Revenue ($)")}</th>
                  <th className="number-col">{t("Allocations")}</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      {t("No data available")}
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr
                      key={item.material}
                      className={
                        selectedMaterials.has(item.material) ? "selected" : ""
                      }
                      onClick={() => handleMaterialToggle(item.material)}
                    >
                      <td className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={selectedMaterials.has(item.material)}
                          onChange={() => handleMaterialToggle(item.material)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="material-name-cell">
                        <div className="material-name-content">
                          <svg width="12" height="12" style={{ flexShrink: 0 }}>
                            <rect
                              x="0"
                              y="0"
                              width="12"
                              height="12"
                              rx="3"
                              fill={COLORS[index % COLORS.length]}
                            />
                          </svg>
                          <span>{item.material}</span>
                        </div>
                      </td>
                      <td className="number-col">
                        {item.quantity.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="number-col">
                        $
                        {item.revenue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="number-col">{item.allocations}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {data.length > 0 && (
                <tfoot>
                  <tr className="totals-row">
                    <td className="checkbox-col"></td>
                    <td>
                      <strong>{t("Total")}</strong>
                    </td>
                    <td className="number-col">
                      <strong>
                        {totals.quantity.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </td>
                    <td className="number-col">
                      <strong>
                        $
                        {totals.revenue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </td>
                    <td className="number-col">
                      <strong>{totals.allocations}</strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Allocation Details - shown when materials are selected */}
      {selectedMaterials.size > 0 && filteredTransactions.length > 0 && (
        <div className="allocation-details-section">
          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-card-icon">
                <FontAwesomeIcon icon={faHistory} />
              </div>
              <h5 className="chart-card-title">
                {t("Delivery Details")} -{" "}
                {Array.from(selectedMaterials).join(", ")}
              </h5>
              <span className="chart-card-badge">
                {filteredTransactions.length} {t("records")}
              </span>
            </div>
            <div className="donor-materials-table-wrapper">
              <table className="donor-materials-table allocation-details-table">
                <thead>
                  <tr>
                    <th>{t("Receiver")}</th>
                    <th>{t("City")}</th>
                    <th>{t("Material")}</th>
                    <th className="number-col">{t("Quantity (lbs)")}</th>
                    <th className="number-col">{t("Revenue ($)")}</th>
                    <th>{t("Delivered")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((txn) => {
                    const timeAgo = getTimeAgo(txn.dateDelivered);
                    const formattedDate = txn.dateDelivered
                      ? new Date(txn.dateDelivered).toLocaleDateString()
                      : "-";

                    return (
                      <tr key={txn.id}>
                        <td className="receiver-name">{txn.receiver}</td>
                        <td>{txn.receiverCity || "-"}</td>
                        <td>{txn.material}</td>
                        <td className="number-col">
                          {txn.quantity.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="number-col">
                          $
                          {txn.revenue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td>
                          <div className="delivered-date-cell">
                            <FontAwesomeIcon
                              icon={faTruck}
                              className="delivered-icon-small"
                            />
                            <div className="delivered-date-info">
                              <span className="delivered-time-ago">
                                {timeAgo}
                              </span>
                              <span className="delivered-full-date">
                                {formattedDate}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MaterialsOverviewSection.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      material: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      revenue: PropTypes.number.isRequired,
      allocations: PropTypes.number.isRequired,
    })
  ).isRequired,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      receiver: PropTypes.string,
      receiverCity: PropTypes.string,
      material: PropTypes.string,
      quantity: PropTypes.number,
      revenue: PropTypes.number,
      dateAllocated: PropTypes.string,
      dateDelivered: PropTypes.string,
      status: PropTypes.string,
    })
  ),
  darkMode: PropTypes.bool,
};

export default MaterialsOverviewSection;
